import { balance, DAY } from './config/balance.ts';
import { assertGame } from './errors.ts';
import { assertCan } from './guards.ts';
import { type ActionResult, type LedgerDelta, type PlayerState, SYSTEM, townName } from './player.ts';

/** Coin locked in the Royal Bank for a fixed term (`PLAYER#<id>` / `LOCK#<id>`). */
export interface BankLock {
  id: string;
  playerId: string;
  amount: number;
  days: number;
  rate: number;
  /** Fixed when the lock is made. */
  interest: number;
  startedAt: number;
  maturesAt: number;
}

const BANK = 'BANK';
export const LOCK_TERMS = Object.keys(balance.bank.lockRates).map(Number);

function atTheBank(p: PlayerState) {
  assertCan(p, 'bank');
  assertGame(p.town === balance.bank.town, 'WRONG_PLACE', `The Royal Bank is in ${townName(balance.bank.town)}.`);
}

const wholeCoin = (amount: number) => assertGame(Number.isInteger(amount) && amount >= 1, 'INVALID', 'Name a whole number of coin.');

export interface BankOutcome {
  amount: number;
  purse: number;
  bank: number;
}

/** Purse → bank. Coin in the bank cannot be robbed. */
export function deposit(player: PlayerState, amount: number): ActionResult<BankOutcome> {
  atTheBank(player);
  wholeCoin(amount);
  assertGame(player.purse >= amount, 'NOT_ENOUGH', `Your purse holds ${player.purse}.`);
  const p = { ...player, purse: player.purse - amount, bank: player.bank + amount };
  return {
    player: p,
    ledger: [
      { kind: 'coin', account: 'purse', delta: -amount, reason: 'deposit', counterparty: BANK },
      { kind: 'coin', account: 'bank', delta: amount, reason: 'deposit', counterparty: BANK },
    ],
    events: [{ type: 'deposit', text: `You deposit ${amount} coin with the Royal Bank.`, data: { amount } }],
    outcome: { amount, purse: p.purse, bank: p.bank },
  };
}

/** Bank → purse. */
export function withdraw(player: PlayerState, amount: number): ActionResult<BankOutcome> {
  atTheBank(player);
  wholeCoin(amount);
  assertGame(player.bank >= amount, 'NOT_ENOUGH', `You have ${player.bank} in the bank.`);
  const p = { ...player, purse: player.purse + amount, bank: player.bank - amount };
  return {
    player: p,
    ledger: [
      { kind: 'coin', account: 'bank', delta: -amount, reason: 'withdrawal', counterparty: BANK },
      { kind: 'coin', account: 'purse', delta: amount, reason: 'withdrawal', counterparty: BANK },
    ],
    events: [{ type: 'withdrawal', text: `You draw ${amount} coin from the Royal Bank.`, data: { amount } }],
    outcome: { amount, purse: p.purse, bank: p.bank },
  };
}

export const lockInterest = (amount: number, days: number) => Math.floor(amount * (balance.bank.lockRates[days] ?? 0));

/** Bank → locked, for 7, 14 or 30 days at the rate of the day. */
export function lockCoin(player: PlayerState, input: { amount: number; days: number; lockId: string; openLocks: number; now: number }): ActionResult<BankLock> & { lock: BankLock } {
  const { amount, days, now } = input;
  const b = balance.bank;
  atTheBank(player);
  wholeCoin(amount);
  assertGame(LOCK_TERMS.includes(days), 'INVALID', `Locks run for ${LOCK_TERMS.join(', ')} days.`);
  assertGame(amount >= b.minLock && amount <= b.maxLock, 'INVALID', `Lock between ${b.minLock} and ${b.maxLock.toLocaleString('en-GB')} coin.`);
  assertGame(input.openLocks < b.maxLocks, 'CONFLICT', `You may hold ${b.maxLocks} locks at once.`);
  assertGame(player.bank >= amount, 'NOT_ENOUGH', `You have ${player.bank} in the bank.`);
  const lock: BankLock = {
    id: input.lockId,
    playerId: player.id,
    amount,
    days,
    rate: b.lockRates[days]!,
    interest: lockInterest(amount, days),
    startedAt: now,
    maturesAt: now + days * DAY,
  };
  return {
    player: { ...player, bank: player.bank - amount, locked: (player.locked ?? 0) + amount },
    ledger: [
      { kind: 'coin', account: 'bank', delta: -amount, reason: 'lock', counterparty: BANK },
      { kind: 'coin', account: 'locked', delta: amount, reason: 'lock', counterparty: BANK },
    ],
    events: [{ type: 'lock', text: `You lock ${amount} coin away for ${days} days, to earn ${lock.interest}.`, data: { amount, days } }],
    outcome: lock,
    lock,
  };
}

/** A matured lock pays out at rollover: the principal back to the bank, plus interest (a SYSTEM faucet). */
export function payLock(player: PlayerState, lock: BankLock, now: number): ActionResult<{ lockId: string; paid: number }> {
  assertGame(lock.playerId === player.id, 'INVALID', 'Not your lock.');
  assertGame(lock.maturesAt <= now, 'INVALID', 'That lock has not matured.');
  const ledger: LedgerDelta[] = [
    { kind: 'coin', account: 'locked', delta: -lock.amount, reason: 'lock-matured', counterparty: BANK },
    { kind: 'coin', account: 'bank', delta: lock.amount, reason: 'lock-matured', counterparty: BANK },
  ];
  if (lock.interest > 0) ledger.push({ kind: 'coin', account: 'bank', delta: lock.interest, reason: 'interest', counterparty: SYSTEM });
  return {
    player: { ...player, locked: (player.locked ?? 0) - lock.amount, bank: player.bank + lock.amount + lock.interest },
    ledger,
    events: [{ type: 'lock-matured', text: `Your ${lock.days}-day lock matures: ${lock.amount} coin and ${lock.interest} interest are back in the bank.`, data: { lockId: lock.id } }],
    outcome: { lockId: lock.id, paid: lock.amount + lock.interest },
  };
}
